import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import { FormInputText } from 'components/form/FormInputText';
import { useDispatch } from 'react-redux';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { filterSelector } from 'helper/filter';

import {
  CREATE_HOTEL_GROUP_MUTATION,
  UPDATE_HOTEL_GROUP_MUTATION,
  LIST_HOTEL_GROUPS_QUERY,
} from './gql';

const schema = yup.object({
  name: yup.string().required(i18next.t('validation-required') as string),
});

interface FormData {
  name: string;
}

interface GroupDialogProps {
  open: boolean;
  onClose: () => void;
  group?: { id: number; name: string } | null;
  spaceId: number | null;
}

export default function GroupDialog({ open, onClose, group, spaceId }: GroupDialogProps) {
  const dispatch = useDispatch();
  const filter = filterSelector();
  const isEdit = !!group;

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (group) {
      reset({ name: group.name });
    } else {
      reset({ name: '' });
    }
  }, [group, reset]);

  const [createGroup] = useMutation(CREATE_HOTEL_GROUP_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, i18next.t('groups-created-success'));
      onClose();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
    refetchQueries: [
      { query: LIST_HOTEL_GROUPS_QUERY, variables: { spaceId: null } }
    ],
  });

  const [updateGroup] = useMutation(UPDATE_HOTEL_GROUP_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, i18next.t('groups-updated-success'));
      onClose();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
    refetchQueries: [
      { query: LIST_HOTEL_GROUPS_QUERY, variables: { spaceId: null } }
    ],
  });

  const onSubmit = async (formData: FormData) => {
    if (isEdit && group) {
      await updateGroup({
        variables: {
          id: group.id,
          data: formData,
        },
      });
    } else {
      await createGroup({
        variables: {
          spaceId: null,
          data: formData,
        },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {isEdit ? i18next.t('groups-edit') : i18next.t('groups-create')}
        </DialogTitle>
        <DialogContent>
          <Box pt={2}>
            <FormInputText
              name="name"
              control={control}
              label={i18next.t('groups-name')}
              helperText={errors.name?.message}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            startIcon={<CancelIcon />}
          >
            {i18next.t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            {i18next.t('save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}